import { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
}

const SIZE_CLASS: Record<string, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
};

export function Modal({ open, onClose, title, children, footer, size = 'md' }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-[2px] transition-opacity duration-200 sm:items-center sm:p-4">
      <div className={`max-h-[90vh] w-full overflow-y-auto rounded-t-3xl bg-surface shadow-float sm:rounded-2xl ${SIZE_CLASS[size] ?? SIZE_CLASS.md}`}>
        {title && (
          <div className="sticky top-0 flex items-center justify-between border-b border-ink-100 bg-surface px-5 py-4">
            <h3 className="text-base font-semibold text-ink-900">{title}</h3>
            <button onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-lg text-ink-400 hover:bg-ink-50" aria-label="Cerrar">
              ✕
            </button>
          </div>
        )}
        <div className="p-5">{children}</div>
        {footer && <div className="border-t border-ink-100 p-4">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
